import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  ListGroup,
  Nav,
  Tab,
  ProgressBar,
} from 'react-bootstrap';
import PageHeader from '../../common/PageHeader';
import './Experience.css';

const Testing = () => {
  const [activeKey, setActiveKey] = useState('traditional');

  const testingFrameworks = [
    { name: 'JUnit', category: 'Java Unit Testing', level: 'Expert', years: '8+ years' },
    { name: 'TestNG', category: 'Java Testing Framework', level: 'Advanced', years: '6+ years' },
    { name: 'Jest', category: 'JavaScript Testing', level: 'Advanced', years: '4+ years' },
    { name: 'Selenium WebDriver', category: 'UI Automation', level: 'Advanced', years: '5+ years' },
    { name: 'Cucumber', category: 'BDD Framework', level: 'Proficient', years: '3+ years' },
    { name: 'Postman/Newman', category: 'API Testing', level: 'Expert', years: '7+ years' },
    { name: 'LoadRunner', category: 'Performance Testing', level: 'Proficient', years: '3+ years' },
  ];

  const aiTestingTools = [
    { name: 'LangSmith', category: 'LLM Evaluation', level: 'Advanced', years: '2+ years' },
    { name: 'Weights & Biases', category: 'ML Experiment Tracking', level: 'Advanced', years: '2+ years' },
    { name: 'pytest', category: 'Python Testing', level: 'Expert', years: '6+ years' },
    { name: 'MLflow', category: 'ML Model Testing', level: 'Proficient', years: '2+ years' },
    { name: 'TensorFlow Extended (TFX)', category: 'ML Pipeline Testing', level: 'Proficient', years: '1+ years' },
    { name: 'Evidently AI', category: 'ML Model Monitoring', level: 'Learning', years: '1+ years' },
  ];

  const methodologies = [
    {
      title: 'Test-Driven Development (TDD)',
      description: 'Implemented TDD practices across Java and Python projects, ensuring code quality and maintainability.',
      technologies: ['JUnit', 'TestNG', 'pytest', 'Mockito']
    },
    {
      title: 'Behavior-Driven Development (BDD)',
      description: 'Applied BDD methodologies using Cucumber for better stakeholder communication and requirement validation.',
      technologies: ['Cucumber', 'Gherkin', 'SpecFlow']
    },
    {
      title: 'Continuous Testing',
      description: 'Integrated automated testing into CI/CD pipelines for rapid feedback and deployment confidence.',
      technologies: ['Jenkins', 'GitLab CI', 'GitHub Actions', 'Docker']
    },
    {
      title: 'Performance Testing',
      description: 'Conducted load and stress testing for enterprise applications to ensure scalability and reliability.',
      technologies: ['LoadRunner', 'JMeter', 'Artillery.js']
    }
  ];

  const aiTestingStrategies = [
    {
      title: 'LLM Evaluation & Validation',
      description: 'Developed comprehensive evaluation frameworks for Large Language Models, focusing on accuracy, consistency, and safety.',
      metrics: ['Accuracy', 'Hallucination Detection', 'Response Quality', 'Bias Assessment']
    },
    {
      title: 'Agentic AI Testing',
      description: 'Created testing strategies for AI agents, including goal achievement, decision-making quality, and multi-step reasoning.',
      metrics: ['Task Completion Rate', 'Decision Accuracy', 'Multi-turn Consistency', 'Tool Usage Efficiency']
    },
    {
      title: 'Model Performance Monitoring',
      description: 'Implemented monitoring systems for ML models in production, tracking drift, performance degradation, and data quality.',
      metrics: ['Model Drift', 'Prediction Accuracy', 'Latency', 'Data Quality Score']
    },
    {
      title: 'AI Safety & Reliability',
      description: 'Developed testing protocols for AI safety, including adversarial testing, robustness evaluation, and failure mode analysis.',
      metrics: ['Robustness Score', 'Safety Compliance', 'Edge Case Handling', 'Failure Recovery']
    }
  ];

  return (
    <>
      <PageHeader title="Testing" />
      <div className="experience-section testing-page" style={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f1f1f1 100%)',
        minHeight: '100vh',
        position: 'relative'
      }}>
        <Container className="pt-4 pb-4">
          <Row className="mb-4">
            <Col>
              <p className="lead text-center mb-4" style={{ color: '#6c757d' }}>
                Comprehensive testing strategies from traditional software validation to cutting-edge AI evaluation
              </p>
            </Col>
          </Row>

      <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-center mb-4">
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '8px',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                display: 'inline-flex',
                gap: '4px'
              }}>
                <button
                  onClick={() => setActiveKey('traditional')}
                  style={{
                    backgroundColor: activeKey === 'traditional' ? '#D4AF37' : 'transparent',
                    color: activeKey === 'traditional' ? '#000' : '#2C2C2C',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: activeKey === 'traditional' ? '0 2px 8px rgba(212, 175, 55, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (activeKey !== 'traditional') {
                      e.target.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeKey !== 'traditional') {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  Traditional Testing
                </button>
                <button
                  onClick={() => setActiveKey('ai-testing')}
                  style={{
                    backgroundColor: activeKey === 'ai-testing' ? '#D4AF37' : 'transparent',
                    color: activeKey === 'ai-testing' ? '#000' : '#2C2C2C',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: activeKey === 'ai-testing' ? '0 2px 8px rgba(212, 175, 55, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (activeKey !== 'ai-testing') {
                      e.target.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeKey !== 'ai-testing') {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  AI/ML Testing
                </button>
                <button
                  onClick={() => setActiveKey('methodologies')}
                  style={{
                    backgroundColor: activeKey === 'methodologies' ? '#D4AF37' : 'transparent',
                    color: activeKey === 'methodologies' ? '#000' : '#2C2C2C',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: activeKey === 'methodologies' ? '0 2px 8px rgba(212, 175, 55, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (activeKey !== 'methodologies') {
                      e.target.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeKey !== 'methodologies') {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  Methodologies
                </button>
              </div>
            </div>
          </Col>
        </Row>

        <Tab.Content>
          <Tab.Pane eventKey="traditional">
            <Row>
              <Col md={6} className="mb-4">
                <Card className="h-100 border-0" style={{ 
                  boxShadow: '0 8px 25px rgba(212, 175, 55, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <Card.Header style={{ 
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)', 
                    color: '#000',
                    border: 'none',
                    padding: '1.5rem'
                  }}>
                    <h5 className="mb-0 fw-bold">Testing Frameworks & Tools</h5>
                  </Card.Header>
                  <Card.Body className="bg-white" style={{ padding: '2rem' }}>
                    {testingFrameworks.map((framework, index) => (
                      <div key={index} className="mb-4">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <span className="fw-semibold text-dark d-block">{framework.name}</span>
                            <div className="text-muted small">{framework.category}</div>
                          </div>
                          <div className="text-end ms-3">
                            <Badge style={{ 
                              backgroundColor: framework.level === 'Expert' ? '#D4AF37' : 
                                             framework.level === 'Advanced' ? '#B8860B' : '#2C2C2C',
                              color: framework.level === 'Expert' ? '#000' : 
                                     framework.level === 'Advanced' ? '#fff' : '#D4AF37',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              fontWeight: '600'
                            }}>
                              {framework.level}
                            </Badge>
                            <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                              {framework.years}
                            </div>
                          </div>
                        </div>
                        <div style={{ height: '1px', backgroundColor: '#e9ecef', margin: '0.5rem 0' }} />
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6} className="mb-4">
                <Card className="h-100 border-0" style={{ 
                  boxShadow: '0 8px 25px rgba(44, 44, 44, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <Card.Header style={{ 
                    background: 'linear-gradient(135deg, #2C2C2C 0%, #1a1a1a 100%)', 
                    color: '#D4AF37',
                    border: 'none',
                    padding: '1.5rem'
                  }}>
                    <h5 className="mb-0 fw-bold">Testing Domains</h5>
                  </Card.Header>
                  <Card.Body className="bg-white" style={{ padding: '2rem' }}>
                    <ListGroup variant="flush">
                      <ListGroup.Item className="d-flex justify-content-between align-items-center border-0 px-0 py-3">
                        <div>
                          <strong className="text-dark">Unit Testing</strong>
                          <div className="text-muted small">Component-level validation</div>
                        </div>
                        <Badge style={{ 
                          background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)', 
                          color: '#000',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          border: 'none'
                        }}>Expert</Badge>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between align-items-center border-0 px-0 py-3">
                        <div>
                          <strong className="text-dark">Integration Testing</strong>
                          <div className="text-muted small">System interaction validation</div>
                        </div>
                        <Badge style={{ 
                          background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)', 
                          color: '#000',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          border: 'none'
                        }}>Expert</Badge>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between align-items-center border-0 px-0 py-3">
                        <div>
                          <strong className="text-dark">API Testing</strong>
                          <div className="text-muted small">RESTful service validation</div>
                        </div>
                        <Badge style={{ 
                          background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)', 
                          color: '#000',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          border: 'none'
                        }}>Expert</Badge>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between align-items-center border-0 px-0 py-3">
                        <div>
                          <strong className="text-dark">UI Automation</strong>
                          <div className="text-muted small">Web application testing</div>
                        </div>
                        <Badge style={{ 
                          backgroundColor: '#2C2C2C', 
                          color: '#D4AF37',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          border: 'none'
                        }}>Advanced</Badge>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between align-items-center border-0 px-0 py-3">
                        <div>
                          <strong className="text-dark">Performance Testing</strong>
                          <div className="text-muted small">Load and stress testing</div>
                        </div>
                        <Badge style={{ 
                          backgroundColor: '#2C2C2C', 
                          color: '#D4AF37',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          border: 'none'
                        }}>Advanced</Badge>
                      </ListGroup.Item>
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="ai-testing">
            <Row className="mb-4">
              <Col md={6} className="mb-4">
                <Card className="h-100 border-0" style={{ 
                  boxShadow: '0 8px 25px rgba(44, 44, 44, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <Card.Header style={{ 
                    background: 'linear-gradient(135deg, #2C2C2C 0%, #1a1a1a 100%)', 
                    color: '#D4AF37',
                    border: 'none',
                    padding: '1.5rem'
                  }}>
                    <h5 className="mb-0 fw-bold">AI/ML Testing Tools</h5>
                  </Card.Header>
                  <Card.Body className="bg-white" style={{ padding: '2rem' }}>
                    {aiTestingTools.map((tool, index) => (
                      <div key={index} className="mb-4">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <span className="fw-semibold text-dark d-block">{tool.name}</span>
                            <div className="text-muted small">{tool.category}</div>
                          </div>
                          <div className="text-end ms-3">
                            <Badge style={{ 
                              backgroundColor: tool.level === 'Expert' ? '#D4AF37' : 
                                             tool.level === 'Advanced' ? '#B8860B' : 
                                             tool.level === 'Proficient' ? '#2C2C2C' : '#6c757d',
                              color: tool.level === 'Expert' ? '#000' : 
                                     tool.level === 'Advanced' ? '#fff' : 
                                     tool.level === 'Proficient' ? '#D4AF37' : '#fff',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              fontWeight: '600'
                            }}>
                              {tool.level}
                            </Badge>
                            <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                              {tool.years}
                            </div>
                          </div>
                        </div>
                        <div style={{ height: '1px', backgroundColor: '#e9ecef', margin: '0.5rem 0' }} />
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6} className="mb-4">
                <Card className="h-100 border-0" style={{ 
                  boxShadow: '0 8px 25px rgba(212, 175, 55, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <Card.Header style={{ 
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)', 
                    color: '#000',
                    border: 'none',
                    padding: '1.5rem'
                  }}>
                    <h5 className="mb-0 fw-bold">AI Testing Focus Areas</h5>
                  </Card.Header>
                  <Card.Body className="bg-white" style={{ padding: '2rem' }}>
                    {aiTestingStrategies.map((strategy, index) => (
                      <Card key={index} className="mb-3 border-0" style={{ 
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }}>
                        <Card.Body className="p-3">
                          <h6 className="fw-bold mb-2" style={{ color: '#D4AF37' }}>{strategy.title}</h6>
                          <p className="small text-muted mb-3">{strategy.description}</p>
                          <div className="d-flex flex-wrap gap-2">
                            {strategy.metrics.map((metric, idx) => (
                              <Badge 
                                key={idx} 
                                style={{ 
                                  backgroundColor: 'transparent', 
                                  color: '#2C2C2C', 
                                  border: '1px solid #D4AF37',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '6px'
                                }} 
                                className="small"
                              >
                                {metric}
                              </Badge>
                            ))}
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="methodologies">
            <Row>
              {methodologies.map((methodology, index) => (
                <Col md={6} key={index} className="mb-4">
                  <Card className="h-100 border-0" style={{ 
                    backgroundColor: '#fff',
                    boxShadow: '0 8px 25px rgba(212, 175, 55, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05)',
                    borderRadius: '12px',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 12px 35px rgba(212, 175, 55, 0.2), 0 6px 15px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(212, 175, 55, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05)';
                  }}>
                    <Card.Body className="p-4">
                      <Card.Title style={{ color: '#D4AF37' }} className="fw-bold mb-3">{methodology.title}</Card.Title>
                      <Card.Text className="text-muted mb-4" style={{ lineHeight: '1.6' }}>
                        {methodology.description}
                      </Card.Text>
                      <div className="d-flex flex-wrap gap-2">
                        {methodology.technologies.map((tech, idx) => (
                          <Badge 
                            key={idx} 
                            style={{ 
                              backgroundColor: 'transparent', 
                              color: '#2C2C2C', 
                              border: '1px solid #B8860B',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '8px',
                              fontSize: '0.8rem'
                            }}
                          >
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <Row className="mt-5">
        <Col>
          <Card className="border-0" style={{ 
            background: 'linear-gradient(135deg, #2C2C2C 0%, #1a1a1a 100%)',
            borderRadius: '16px',
            boxShadow: '0 12px 35px rgba(44, 44, 44, 0.3), 0 6px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <Card.Body className="text-center py-5 px-4">
              <h4 className="fw-bold mb-4" style={{ 
                color: '#D4AF37',
                fontSize: '1.8rem',
                letterSpacing: '0.5px'
              }}>Testing Philosophy</h4>
              <p className="mb-0" style={{ 
                color: '#fff',
                fontSize: '1.1rem',
                lineHeight: '1.7',
                fontStyle: 'italic',
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                "Quality is not an act, it is a habit. Through comprehensive testing strategies that evolve with technology, 
                I ensure robust, reliable, and safe software systems that users can trust."
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      </Container>
    </div>
    </>
  );
};

export default Testing;